import React, { useState, useCallback } from 'react';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  ContentCopyOutlined,
  Check as CheckIcon,
  ThumbUpAlt,
  ThumbUpAltOutlined,
  ThumbDownAlt,
  ThumbDownAltOutlined,
  MoreHoriz as MoreIcon,
  NotesOutlined as PlainTextIcon,
  CodeOutlined as MarkdownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useSetMessageFeedback } from '../../hooks/api/useConversations';
import type { ChatSource } from '../../api/chatTypes';
import { buildCopyPayload, writeClipboard } from './copyAnswer';

type CopyFlavor = 'rich' | 'markdown' | 'plain';

export interface MessageActionsProps {
  /** Plain-text content of the assistant message (may include [[N]] markers). */
  content: string;
  /** Cited sources of the message; listed under the copied answer. */
  sources?: ChatSource[];
  /** The persisted message id. When undefined the message is still streaming. */
  messageId?: string;
  /** The conversation this message belongs to. */
  conversationId?: string;
  /** The current server-side feedback value for this message. */
  feedback?: string | null;
}

/**
 * Compact action row rendered at the bottom-right of every assistant message.
 * Provides: copy-to-clipboard (rich text plus Markdown in one click, plain
 * text and Markdown-only via the small menu), thumbs-up, thumbs-down.
 *
 * Copying keeps the citation markers readable as [N] and appends the cited
 * sources with their pages, so a pasted answer stays verifiable.
 *
 * Thumbs-up / thumbs-down send optimistic UI updates via useSetMessageFeedback;
 * clicking the already-active button toggles it off (sends null).
 */
export const MessageActions: React.FC<MessageActionsProps> = ({
  content,
  sources = [],
  messageId,
  conversationId,
  feedback,
}) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const feedbackMutation = useSetMessageFeedback();

  // Local copy-success flash state
  const [copied, setCopied] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Optimistic local feedback state — initialised from the server value so
  // the correct thumb renders immediately on mount.
  const [localFeedback, setLocalFeedback] = useState<string | null>(feedback ?? null);

  // Keep local state in sync when the prop changes (e.g. after cache update).
  React.useEffect(() => {
    setLocalFeedback(feedback ?? null);
  }, [feedback]);

  const copyWith = useCallback(
    (flavor: CopyFlavor) => {
      setMenuAnchor(null);
      const payload = buildCopyPayload(content, sources, {
        sourcesHeading: t('chat.actions.sourcesHeading'),
        pageAbbrev: t('chat.actions.pageAbbrev'),
      });
      // Rich copy: HTML for Office targets, Markdown in the text flavor.
      const request =
        flavor === 'rich'
          ? { html: payload.html, text: payload.markdown }
          : { text: flavor === 'markdown' ? payload.markdown : payload.text };
      // The clipboard write must start inside the click gesture (Safari).
      writeClipboard(request)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          showError(t('chat.actions.copyFailed'));
        });
    },
    [content, sources, showError, t]
  );

  const handleCopy = useCallback(() => copyWith('rich'), [copyWith]);

  const handleThumb = useCallback(
    (value: 'up' | 'down') => {
      if (!messageId || !conversationId) {return;}

      // Toggle off if the same thumb is clicked again.
      const next: string | null = localFeedback === value ? null : value;

      // Optimistic update
      setLocalFeedback(next);

      feedbackMutation.mutate(
        { conversationId, messageId, feedback: next },
        {
          onError: () => {
            // Revert optimistic update on failure.
            setLocalFeedback(feedback ?? null);
            showError(t('chat.actions.feedbackError'));
          },
        }
      );
    },
    [messageId, conversationId, localFeedback, feedback, feedbackMutation, showError, t]
  );

  const thumbsDisabled = !messageId || !conversationId;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.25,
        mt: 0.75,
        mr: -0.5,
      }}
    >
      {/* Copy button */}
      <Tooltip
        title={copied ? t('chat.actions.copied') : t('chat.actions.copy')}
        placement="top"
        arrow
      >
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label={copied ? t('chat.actions.copied') : t('chat.actions.copy')}
          sx={{
            color: copied ? '#4caf50' : '#6b7480',
            padding: '4px',
            '&:hover': {
              color: '#003064',
              backgroundColor: 'rgba(0, 48, 100, 0.06)',
            },
            '&:focus-visible': {
              outline: '2px solid #003064',
              outlineOffset: '2px',
            },
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: '0.95rem' }} />
          ) : (
            <ContentCopyOutlined sx={{ fontSize: '0.95rem' }} />
          )}
        </IconButton>
      </Tooltip>
      <span role="status" aria-live="polite" style={visuallyHidden as React.CSSProperties}>
        {copied ? t('chat.actions.copied') : ''}
      </span>

      {/* Further copy flavors: plain text (Markdown removed) and Markdown only */}
      <Tooltip title={t('chat.actions.moreCopyOptions')} placement="top" arrow>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label={t('chat.actions.moreCopyOptions')}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          sx={{
            color: '#6b7480',
            padding: '4px',
            ml: -0.5,
            '&:hover': { color: '#003064', backgroundColor: 'rgba(0, 48, 100, 0.06)' },
            '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '2px' },
          }}
        >
          <MoreIcon sx={{ fontSize: '0.95rem' }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={() => copyWith('plain')} dense>
          <ListItemIcon>
            <PlainTextIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('chat.actions.copyPlain')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => copyWith('markdown')} dense>
          <ListItemIcon>
            <MarkdownIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('chat.actions.copyMarkdown')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Thumbs-up button */}
      <Tooltip title={t('chat.actions.thumbsUp')} placement="top" arrow>
        <span>
          <IconButton
            size="small"
            disabled={thumbsDisabled}
            onClick={() => handleThumb('up')}
            aria-label={t('chat.actions.thumbsUp')}
            aria-pressed={localFeedback === 'up'}
            sx={{
              color:
                localFeedback === 'up'
                  ? '#2e7d32'
                  : '#6b7480',
              padding: '4px',
              '&:hover:not(:disabled)': {
                color: '#003064',
                backgroundColor: 'rgba(0, 48, 100, 0.06)',
              },
              '&:focus-visible': {
                outline: '2px solid #003064',
                outlineOffset: '2px',
              },
              '&.Mui-disabled': {
                color: 'rgba(107, 116, 128, 0.35)',
              },
            }}
          >
            {localFeedback === 'up' ? (
              <ThumbUpAlt sx={{ fontSize: '0.95rem' }} />
            ) : (
              <ThumbUpAltOutlined sx={{ fontSize: '0.95rem' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Thumbs-down button */}
      <Tooltip title={t('chat.actions.thumbsDown')} placement="top" arrow>
        <span>
          <IconButton
            size="small"
            disabled={thumbsDisabled}
            onClick={() => handleThumb('down')}
            aria-label={t('chat.actions.thumbsDown')}
            aria-pressed={localFeedback === 'down'}
            sx={{
              color:
                localFeedback === 'down'
                  ? '#c62828'
                  : '#6b7480',
              padding: '4px',
              '&:hover:not(:disabled)': {
                color: '#003064',
                backgroundColor: 'rgba(0, 48, 100, 0.06)',
              },
              '&:focus-visible': {
                outline: '2px solid #003064',
                outlineOffset: '2px',
              },
              '&.Mui-disabled': {
                color: 'rgba(107, 116, 128, 0.35)',
              },
            }}
          >
            {localFeedback === 'down' ? (
              <ThumbDownAlt sx={{ fontSize: '0.95rem' }} />
            ) : (
              <ThumbDownAltOutlined sx={{ fontSize: '0.95rem' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
